// src/components/reservations/CreateReservation.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
  ShieldCheck,
  ChevronRight
} from 'lucide-react';
// Date picker imports
import { FothDateTimePicker } from '../common/DatePicker';
import { getFeedOfferDetail as getOfferDetails, createReservation } from '../../services/api';
import './CreateReservation.scss';

// Animation variants following FOTH design system
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.23, 1, 0.32, 1],
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.23, 1, 0.32, 1] }
  }
};

const CreateReservation = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [startDatetime, setStartDatetime] = useState(null);
  const [endDatetime, setEndDatetime] = useState(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offer, setOffer] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Fetch offer details using standardized API service
  useEffect(() => {
    const fetchOfferDetails = async () => {
      try {
        const response = await getOfferDetails(offerId);
        setOffer(response.data);
      } catch (err) {
        console.error('Error fetching offer details:', err);
      }
    };

    if (offerId) {
      fetchOfferDetails();
    }
  }, [offerId]);

  // Validation function
  const validateForm = () => {
    const errors = {};
    
    if (!startDatetime) {
      errors.startDatetime = 'La fecha y hora de inicio es requerida';
    }
    
    if (!endDatetime) {
      errors.endDatetime = 'La fecha y hora de fin es requerida';
    }
    
    if (startDatetime && endDatetime) {
      const now = new Date();
      
      if (startDatetime < now) {
        errors.startDatetime = 'La fecha de inicio debe ser futura';
      }
      
      if (endDatetime <= startDatetime) {
        errors.endDatetime = 'La fecha de fin debe ser posterior al inicio';
      }
      
      const duration = (endDatetime - startDatetime) / (1000 * 60); // minutes
      if (duration < 30) {
        errors.endDatetime = 'La reservación debe durar al menos 30 minutos';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await createReservation({
        offer: offerId,
        start_datetime: startDatetime ? startDatetime.toISOString() : null,
        end_datetime: endDatetime ? endDatetime.toISOString() : null,
        notes: notes,
      });

      setSuccess(response.data.status === 'confirmed'
        ? '¡Reservación confirmada! Tu espacio ha sido reservado.'
        : 'Solicitud enviada. Tu reserva está pendiente de aprobación del negocio.');
      setTimeout(() => navigate('/creator/reservations'), 2500);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || err.message || 'Error al crear la reservación');
    } finally {
      setLoading(false);
    }
  };

  const calculateDuration = () => {
    if (startDatetime && endDatetime) {
      const duration = (endDatetime - startDatetime) / (1000 * 60 * 60); // hours
      return duration > 0 ? `${duration.toFixed(1)} horas` : '';
    }
    return '';
  };

  return (
    <motion.div 
      className="create-reservation-container enhanced"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="reservation-content-wrapper">
        {/* Left Side: Information & Visual */}
        <motion.div className="reservation-sidebar" variants={itemVariants}>
          <button 
            onClick={() => navigate(-1)}
            className="btn-back-minimal"
          >
            <ArrowLeft size={16} />
            <span>Volver</span>
          </button>

          <div className="sidebar-header">
            <div className="luxury-badge">
              <Sparkles size={14} />
              <span>Nueva Reserva</span>
            </div>
            <h1 className="editorial-title">Agenda tu Ejecución</h1>
            <p className="editorial-subtitle">Asegura tu lugar en la colaboración seleccionada.</p>
          </div>

          {offer && (
            <div className="offer-summary-card">
              <div className="card-image">
                {offer.featured_image_url ? (
                  <img src={offer.featured_image_url} alt={offer.title} />
                ) : (
                  <div className="placeholder-gradient" />
                )}
              </div>
              <div className="card-info">
                <h3>{offer.title}</h3>
                <p>{offer.business_name}</p>
              </div>
            </div>
          )}

          <div className="reservation-tips-luxury">
            <div className="tip-item">
              <ShieldCheck size={18} />
              <div>
                <h5>Confirmación Inmediata</h5>
                <p>Tu reserva se bloquea automáticamente en el calendario.</p>
              </div>
            </div>
            <div className="tip-item">
              <Clock size={18} />
              <div>
                <h5>Puntualidad</h5>
                <p>Respeta el horario acordado para mantener tu reputación.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right Side: Form */}
        <motion.div className="reservation-form-main" variants={itemVariants}>
          <form onSubmit={handleSubmit} className="luxury-form">
            <div className="form-section">
              <h4 className="section-title">Detalles de Tiempo</h4>
              <div className="form-grid">
                <div className="form-group">
                  <FothDateTimePicker
                    value={startDatetime}
                    onChange={(date) => {
                      setStartDatetime(date);
                      setValidationErrors(prev => ({ ...prev, startDatetime: null }));
                    }}
                    label="Fecha y Hora de Inicio"
                    placeholder="Selecciona..."
                    error={validationErrors.startDatetime}
                    timeIntervals={15}
                    minDate={new Date()}
                    disabled={loading}
                    required={true}
                  />
                </div>

                <div className="form-group">
                  <FothDateTimePicker
                    value={endDatetime}
                    onChange={(date) => {
                      setEndDatetime(date);
                      setValidationErrors(prev => ({ ...prev, endDatetime: null }));
                    }}
                    label="Fecha y Hora de Fin"
                    placeholder="Selecciona..."
                    error={validationErrors.endDatetime}
                    timeIntervals={15}
                    minDate={startDatetime || new Date()}
                    disabled={loading || !startDatetime}
                    required={true}
                  />
                </div>
              </div>

              <AnimatePresence>
                {calculateDuration() && (
                  <motion.div 
                    className="duration-badge"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                  >
                    <Clock size={14} />
                    <span>Duración total: {calculateDuration()}</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="form-section">
              <h4 className="section-title">Comentarios Adicionales</h4>
              <div className="form-group">
                <textarea
                  id="notes"
                  className="luxury-textarea"
                  rows="4"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ej: Requerimientos técnicos, ubicación específica, etc."
                  disabled={loading}
                />
                <p className="field-hint">Cualquier detalle que ayude al negocio a prepararse para tu visita.</p>
              </div>
            </div>

            <div className="form-actions">
              <motion.button 
                type="submit" 
                className="btn-luxury-submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <div className="loader-dots"><span></span><span></span><span></span></div>
                ) : (
                  <>
                    <span>Confirmar Reservación</span>
                    <ChevronRight size={18} />
                  </>
                )}
              </motion.button>
            </div>
          </form>

          <AnimatePresence>
            {success && (
              <motion.div 
                className="toast-message success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <CheckCircle2 size={20} />
                <span>{success}</span>
              </motion.div>
            )}

            {error && (
              <motion.div 
                className="toast-message error"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <XCircle size={20} />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default CreateReservation;
